import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import Tooltip from '@material-ui/core/Tooltip';
import {HiOutlineUserGroup} from '@react-icons/all-files/hi/HiOutlineUserGroup';
import {TiGroup} from '@react-icons/all-files/ti/TiGroup';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import classnames from 'classnames';
import stringSimilarity from 'string-similarity';
import {isVsFeatureEnabled} from '@utils/helpers';
import {PeopleIcon} from '@components/shared/icons/PeopleIcon';
import {AppIconButton} from '@components/Core/buttons/AppIconButton/AppIconButton';
import {EmptyState} from '@components/shared/EmptyState';
import {SkeletonText} from '@components/shared/Skeleton';
import {
    fetchUsers,
    selectUsers,
    selectIsFetchingUsers,
    delegateUser,
    stopDelegation
} from './delegationSlice';
import {
    selectDelegationUser, selectFeatures, selectUser, selectVSFeatures
} from '../Login/userSlice';
import {BROADCAST_MESSAGES, DASHBOARD_PATH, VS_FEATURES} from '../../../constants';
import Loading from '../shared/Loading/Loading';
import Search from '../shared/Search/Search';
import useBroadcaster from '../../hooks/useBroadcaster';
import './delegation.less';

export default function DelegationSelector() {
    const intl = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const broadcast = useBroadcaster();
    const userData = useSelector(selectUser);
    const users = useSelector(selectUsers);
    const isFetchingUsers = useSelector(selectIsFetchingUsers);
    const delegatedUser = useSelector(selectDelegationUser);
    const isFatIcons = get(userData, 'data.fatIcons');
    const [user, setUser] = useState('');
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isDelegated = !isEmpty(delegatedUser);
    const searchedUsers = !isEmpty(users) ? users.filter(({name = '', email = ''}) => {
        const isNameMatch = stringSimilarity
            .compareTwoStrings(name.toLowerCase(), search.toLowerCase());
        const isEmailMatch = stringSimilarity
            .compareTwoStrings(email.toLowerCase(), search.toLowerCase());
        return search === '' || search.length < 3 || isNameMatch >= 0.3 || isEmailMatch >= 0.3;
    }) : [];
    const iconClassName = classnames('delegation-icon', {
        'is-delegated': isDelegated
    });

    const features = useSelector(selectFeatures);
    const isDelegationEnabled = features?.delegation;
    const vsFeatures = useSelector(selectVSFeatures);
    const isDesignV2 = isVsFeatureEnabled({feature: VS_FEATURES.DESIGN_V2, vsFeatures});
    const changeUser = (event) => {
        setUser(event.target.value);
    };

    useEffect(() => {
        if (isDelegationEnabled) {
            dispatch(fetchUsers());
        }
    }, [isDelegationEnabled]);

    function open() {
        setIsModalOpen(true);
    }

    function close() {
        setSearch('');
        setIsModalOpen(false);
    }

    async function delegate() {
        setIsLoading(true);
        const wasSuccessful = await dispatch(delegateUser(user));
        if (wasSuccessful) {
            close();
            broadcast(BROADCAST_MESSAGES.DELEGATION_CHANGED);
            navigate(DASHBOARD_PATH, {replace: true});
        }
        setIsLoading(false);
    }

    async function stopUserDelegation() {
        setIsLoading(true);
        const wasSuccessful = await dispatch(stopDelegation());
        if (wasSuccessful) {
            setUser('');
            close();
            broadcast(BROADCAST_MESSAGES.DELEGATION_CHANGED);
            navigate(DASHBOARD_PATH, {replace: true});
        }
        setIsLoading(false);
    }

    function changeSearch(value) {
        setSearch(value);
    }
    function renderIcon() {
        if (isDesignV2) {
            return <PeopleIcon />;
        }
        if (isFatIcons) {
            return <TiGroup className={iconClassName} />;
        }
        return <HiOutlineUserGroup className={iconClassName} />;
    }

    if (isEmpty(users) && !isDelegated) {
        return null;
    }

    function renderModal() {
        return (
            <Dialog
                maxWidth="md"
                open={isModalOpen}
                classes={{root: 'delegation-modal'}}
                onClose={close}
            >
                <Loading isLoading={isLoading} />
                <DialogTitle>
                    {intl.formatMessage({id: 'esl.generic.manage_delegation', defaultMessage: ''})}
                </DialogTitle>
                <DialogContent>
                    {isDelegated && (
                        <div className="current-delegation-info">
                            {intl.formatMessage({id: 'esl.generic.manage_transactions_for', defaultMessage: ''}, {
                                name: userData?.name || `${userData?.firstName || ''} ${userData?.lastName || ''}`
                            })}
                        </div>
                    )}
                    {!isDelegated && (
                        <>
                            <p className="delegation-description">
                                {intl.formatMessage({id: 'esl.generic.manage_delegation_description', defaultMessage: ''})}
                            </p>
                            <Search onChange={changeSearch} />
                            {isFetchingUsers && isEmpty(users) ? (
                                <div className="users-skeleton" aria-busy="true">
                                    <SkeletonText lines={4} />
                                </div>
                            ) : (
                                <div className="users-container">
                                    <RadioGroup aria-label="delegation users" name="users" value={user} onChange={changeUser}>
                                        {searchedUsers.map(({id, email, name}) => (
                                            <FormControlLabel
                                                value={id}
                                                control={<Radio size="small" />}
                                                key={`deledated-user-${id}`}
                                                label={(
                                                    <div className="user">
                                                        <div className="user-name">{name}</div>
                                                        <div className="user-email">{email}</div>
                                                    </div>
                                                )}
                                            />
                                        ))}
                                    </RadioGroup>
                                </div>
                            )}
                            {!isFetchingUsers && isEmpty(searchedUsers) && search !== '' && (
                                <EmptyState
                                    icon={<HiOutlineUserGroup />}
                                    title={intl.formatMessage({id: 'esl.feedback.no_result', defaultMessage: 'No result found'})}
                                    description={intl.formatMessage({id: 'esl.feedback.sender_not_found', defaultMessage: ''})}
                                    size="sm"
                                    className="delegation-empty"
                                />
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <AppOutlinedButton onClick={close} className="cancel-btn">
                        {intl.formatMessage({id: 'esl.generic.cancel', defaultMessage: ''})}
                    </AppOutlinedButton>
                    {isDelegated ? (
                        <AppButton
                            onClick={stopUserDelegation}
                            className="stop-delegation-btn"
                            disabled={isLoading}
                        >
                            {intl.formatMessage({id: 'esl.generic.stop_managing', defaultMessage: ''})}
                        </AppButton>
                    ) : (
                        <AppButton
                            onClick={delegate}
                            className="delegate-btn"
                            disabled={!user || isLoading}
                        >
                            {intl.formatMessage({id: 'esl.generic.start_managing', defaultMessage: ''})}
                        </AppButton>
                    )}
                </DialogActions>
            </Dialog>
        );
    }

    const button = (
        <AppIconButton
            onClick={open}
        >
            {renderIcon()}
        </AppIconButton>
    );

    return (
        <div className={classnames('delegation-icon-container', {
            v2: isDesignV2
        })}
        >
            {isDelegated
                ? (
                    <Tooltip
                        title={intl.formatMessage({id: 'esl.generic.manage_transactions_for', defaultMessage: ''}, {
                            name: userData?.name || `${userData?.firstName || ''} ${userData?.lastName || ''}`
                        })}
                        arrow
                    >
                        {button}
                    </Tooltip>
                )
                : button}
            {renderModal()}
        </div>
    );
}
